<script setup lang="ts" generic="T extends string">
import { useResizeObserver } from '@vueuse/core';
import { onMounted, ref, useTemplateRef, watch } from 'vue';

import { useScrollFade } from '@/shared/lib/composables';

/**
 * Сегментированный переключатель вкладок в одну строку: не влезающие вкладки прокручиваются
 * по горизонтали без видимого скроллбара, край со скрытыми вкладками затухает.
 * Подложка активной вкладки переезжает к новой, а не перескакивает.
 * `aria-label` и классы раскладки передаёт вызывающий. `count` рисуется бейджем, ноль скрыт;
 * `danger` красит бейдж в цвет ошибки.
 */
const props = defineProps<{
  items: readonly { id: T; label: string; count?: number | undefined; danger?: boolean }[];
}>();

const active = defineModel<T>({ required: true });
const list = useTemplateRef<HTMLElement>('list');
const { maskClass, updateFade, reveal } = useScrollFade(list, { axis: 'x' });
const indicator = ref<{ left: number; width: number } | null>(null);

/** Ставит подложку под активную вкладку и возвращает вкладку. */
const measureIndicator = (): HTMLElement | null | undefined => {
  const tab = list.value?.querySelector<HTMLElement>('[aria-selected="true"]');
  indicator.value = tab ? { left: tab.offsetLeft, width: tab.offsetWidth } : null;

  return tab;
};

/** Докручивает строку до активной вкладки, не трогая прокрутку страницы. */
const syncActive = (): void => reveal(measureIndicator());

onMounted(syncActive);
watch(active, syncActive, { flush: 'post' });
watch(() => props.items, syncActive, { flush: 'post', deep: true });
// Строка, показанная из скрытого состояния, меряется заново: в display:none размеры нулевые.
useResizeObserver(list, measureIndicator);
</script>

<template>
  <div
    class="flex max-w-full rounded-lg bg-(--surface-sunken)"
    role="tablist"
  >
    <div
      ref="list"
      class="relative flex min-w-0 [scrollbar-width:none] gap-1 overflow-x-auto p-1 [&::-webkit-scrollbar]:hidden"
      :class="maskClass"
      @scroll.passive="updateFade"
    >
      <div
        v-if="indicator"
        aria-hidden="true"
        class="absolute top-1 bottom-1 left-0 rounded-md bg-(--surface-card) shadow-sm ring-1 ring-(--border-soft) transition-[translate,width] duration-[260ms] ease-snappy motion-reduce:transition-none"
        :style="{ translate: `${indicator.left}px 0`, width: `${indicator.width}px` }"
      />
      <button
        v-for="item in items"
        :key="item.id"
        type="button"
        role="tab"
        class="relative min-h-11 shrink-0 rounded-md px-4 text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-(--accent) focus-visible:outline-none"
        :class="active === item.id ? 'text-(--text-primary)' : 'text-(--text-secondary) hover:text-(--text-primary)'"
        :aria-selected="active === item.id"
        @click="active = item.id"
      >
        {{ item.label }}
        <span
          v-if="item.count"
          class="ml-1.5 rounded-full px-1.5 py-0.5 text-xs tabular-nums"
          :class="item.danger ? 'bg-(--danger)/15 text-(--danger)' : 'bg-(--surface-strong) text-(--text-secondary)'"
          >{{ item.count }}</span
        >
      </button>
    </div>
  </div>
</template>
